import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import DialogTitle from '../shared/DialogTitle';
import ServerError from '../error/ServerError';
import Loading from '../shared/Loading';
import { ASSIGN_MEAL_PLAN, GET_MEAL_PLANS } from '../../graphql/nutrition';

export default function AssignMealPlanDialog({
  open,
  onClose,
  trainerId,
  user,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedMealPlan, setSelectedMealPlan] = useState(null);
  const [startedAt, setStartedAt] = useState('');

  const { loading, error, data } = useQuery(GET_MEAL_PLANS, {
    variables: { input: { trainerId, limit: 500 } },
    skip: !open || !trainerId,
  });

  const [assignMealPlan, { loading: saving }] = useMutation(ASSIGN_MEAL_PLAN, {
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    },
    onCompleted: (data) => {
      enqueueSnackbar('Meal plan assigned. Member notification will be sent.', {
        variant: 'success',
      });
      setSelectedMealPlan(null);
      setStartedAt('');
      onClose(data?.assignMealPlan);
    },
  });

  const handleSubmit = async () => {
    if (!selectedMealPlan?._id) {
      enqueueSnackbar('Select a meal plan first.', { variant: 'warning' });
      return;
    }

    await assignMealPlan({
      variables: {
        input: {
          userId: user._id,
          mealPlanId: selectedMealPlan._id,
          startedAt: startedAt || null,
        },
      },
    });
  };

  if (error) {
    return <ServerError message={error.message || 'Load failed'} />;
  }

  return (
    <Dialog onClose={() => onClose()} open={open} fullWidth maxWidth="sm">
      <DialogTitle onClose={() => onClose()}>
        Assign Existing Meal Plan
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Loading />}
        {!loading && (
          <Box display="flex" flexDirection="column" gap={3} mt={1}>
            <Autocomplete
              options={data?.mealPlans || []}
              getOptionLabel={(option) => option?.title || ''}
              value={selectedMealPlan}
              onChange={(event, value) => setSelectedMealPlan(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Meal plan"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Start date"
              type="date"
              value={startedAt}
              onChange={(event) => setStartedAt(event.target.value)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          color="primary"
          disabled={saving || !selectedMealPlan?._id}
          onClick={handleSubmit}
          variant="contained"
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
